import {Component, OnInit} from '@angular/core';
import {combineLatest, iif, Observable, of, Subject} from 'rxjs';
import {FormControl} from '@angular/forms';
import {debounceTime, exhaustMap, map, startWith, takeUntil, tap} from 'rxjs/operators';
import {SuppliersService} from '../../../services/suppliers.service';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {CommissionBottomsheetComponent} from '../overlays/commission-bottomsheet/commission.bottomsheet.component';
import {Store} from '@ngrx/store';
import {ProleisSuppliersState} from '../../store/store.index';
import {OrdersActions, OrdersSelectors} from '../../store/orders/orders.index';
import {MatDialog} from '@angular/material/dialog';
import {SearchPositionDialogComponent} from '../search-position/search.position.dialog.component';
import {ProleisObject} from "@proleis/proleis-rest-client";
import {CurrentViewService} from "@proleis/proleis-web-app";

@Component({
  selector: 'ps-responsive-notes',
  templateUrl: 'orders.list.component.html'
})
export class OrdersListComponent implements OnInit {

  destroy$: Subject<any> = new Subject<any>();
  deliveryPbos$: Observable<ProleisObject[]>;
  isLoading: boolean;
  searchFormControl: FormControl;
  filterStart: Date;
  filterEnd: Date;
  filterAll: boolean;
  filterChanged: boolean;
  filtered = false;
  filterChannel: string | undefined;
  channels: ProleisObject[];

  constructor(private cvs: CurrentViewService, private bs: MatBottomSheet,
              private store: Store<ProleisSuppliersState>, public suppliersService: SuppliersService,
              private matDialog: MatDialog) {
  }

  ngOnInit() {
    this.searchFormControl = new FormControl();

    const ordersFilter = localStorage.getItem('ps-orders-filter');
    if (ordersFilter) {
      this.searchFormControl.setValue(ordersFilter);
    }

    this.store.dispatch(OrdersActions.LOAD_CHANNELS());
    this.store.select(OrdersSelectors.selectChannels).pipe(
      takeUntil(this.destroy$),
      map(channels => this.channels = channels)
    ).subscribe();

    this.store.select(OrdersSelectors.selectAreOrdersLoading).pipe(
      takeUntil(this.destroy$),
      map(isLoading => this.isLoading = isLoading)
    ).subscribe();

    this.initFilter();
    this.updatePbos();
  }


  updatePbos() {
    this.loadDeliveryPbos();
    const loadPbos$ = this.store.select(OrdersSelectors.selectOrders);
    const filter$ = this.filter();

    this.deliveryPbos$ = combineLatest([loadPbos$, filter$])
      .pipe(
        exhaustMap(([pbos, filterExpression]) => {
          return iif(() => filterExpression === '',
            of(pbos.data),
            of(pbos.data.filter(
              pbo => {
                if (!filterExpression) {
                  return true;
                }

                let matched = false;
                if (pbo.NAME) {
                  matched = pbo.NAME.toLowerCase().includes(filterExpression.toLowerCase());
                }

                if (!matched && pbo.TEXT) {
                  matched = pbo.TEXT.toLowerCase().includes(filterExpression.toLowerCase());
                }

                if (!matched && pbo.LIEFERANT) {
                  matched = pbo.LIEFERANT.toLowerCase().includes(filterExpression.toLowerCase());
                }

                if (!matched && pbo.EXTBESTELLNR) {
                  matched = pbo.EXTBESTELLNR.toLowerCase().includes(filterExpression.toLowerCase());
                }

                if (!matched && pbo.CHANNELNAME) {
                  matched = pbo.CHANNELNAME.toLowerCase().includes(filterExpression.toLowerCase());
                }

                if (!matched && pbo.PROJEKTNAME) {
                  matched = pbo.PROJEKTNAME.toLowerCase().includes(filterExpression.toLowerCase());
                }

                return matched;
              })
            ));
        })
      );
  }

  loadDeliveryPbos() {
    this.store.dispatch(OrdersActions.LOAD_ORDERS({
      filterChanged: this.filterChanged,
      filterAll: this.filterAll,
      filterChannel: this.filterChannel,
      filterStart: this.filterStart,
      filterEnd: this.filterEnd
    }));
  }

  filter(): Observable<string> {
    return this.searchFormControl.valueChanges.pipe(
      tap(value => localStorage.setItem('ps-orders-filter', value)),
      debounceTime(250),
      startWith(localStorage.getItem('ps-orders-filter'))
    );
  }

  executeFilter() {
    this.filtered = true;
    this.saveFilter();
    this.updatePbos();
  }

  initFilter() {
    this.filterAll = JSON.parse(localStorage.getItem('ps-orders-filter-all')) !== 'null' ?
      JSON.parse(localStorage.getItem('ps-orders-filter-all')) : null;
    this.filterStart = localStorage.getItem('ps-orders-filter-start') !== 'null' &&
    localStorage.getItem('ps-orders-filter-start') !== null ?
      new Date(localStorage.getItem('ps-orders-filter-start')) : null;
    this.filterEnd = localStorage.getItem('ps-orders-filter-end') !== 'null' &&
    localStorage.getItem('ps-orders-filter-end') !== null ?
      new Date(localStorage.getItem('ps-orders-filter-end')) : null;
    this.filterChannel = localStorage.getItem('ps-orders-filter-channel') !== 'null' ?
      localStorage.getItem('ps-orders-filter-channel') : null;
    this.filterChanged = localStorage.getItem('ps-orders-filter-changed') !== 'null' ?
      JSON.parse(localStorage.getItem('ps-orders-filter-changed')) : null;

    if (this.filterAll || this.filterStart || this.filterEnd || this.filterChannel || this.filterChanged) {
      this.filtered = true;
    }
  }

  saveFilter() {
    if (this.filterStart) {
      localStorage.setItem('ps-orders-filter-start', this.filterStart.toISOString());
    }

    if (this.filterEnd) {
      localStorage.setItem('ps-orders-filter-end', this.filterEnd.toISOString());
    }

    if (this.filterChannel) {
      localStorage.setItem('ps-orders-filter-channel', this.filterChannel);
    }

    localStorage.setItem('ps-orders-filter-all', String(this.filterAll));
    localStorage.setItem('ps-orders-filter-changed', String(this.filterChanged));
  }

  toggleFilter() {
    this.cvs.drawer.toggle();
  }

  resetFilter() {
    this.filterAll = null;
    this.filterEnd = null;
    this.filterStart = null;
    this.filtered = false;
    this.filterChannel = null;
    this.filterChanged = null;
    localStorage.setItem('ps-orders-filter-all', null);
    localStorage.setItem('ps-orders-filter-start', null);
    localStorage.setItem('ps-orders-filter-end', null);
    localStorage.setItem('ps-orders-filter-channel', null);
    localStorage.setItem('ps-orders-filter-changed', null);
    this.updatePbos();
  }

  setCommissionNr(objId: string) {
    this.bs.open(CommissionBottomsheetComponent, {
      data: objId
    }).afterDismissed().subscribe(() => this.updatePbos());
  }

  compareChannels(o1: ProleisObject, o2: ProleisObject) {
    return o1.OBJECT_ID === o2.OBJECT_ID;
  }

  getFilterBadgeCount() {
    let count = 0;

    if (this.filterChanged) {
      count++;
    }

    if (this.filterAll) {
      count++;
    }

    if (this.filterStart) {
      count++;
    }

    if (this.filterEnd) {
      count++;
    }

    if (this.filterChannel) {
      count++;
    }

    return count;
  }

  openSearchDialog() {
    this.matDialog.open(SearchPositionDialogComponent);
  }

}
